import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { TariffVersionService } from '../../../api/gen';
import {
  AddGracePeriodRequest,
  AddRateBandRequest,
  AddRateCapRequest,
  AddRateTierRequest,
  AddTariffVersionRequest,
  AUTHORABLE_CAP_ANCHOR,
  BILLING_INTERVALS,
  BillingInterval,
  CAP_WINDOWS,
  CapWindow,
  DEFAULT_TARIFF_TIME_ZONE,
  GRACE_BEHAVIORS,
  GRACE_TRIGGERS,
  GraceBehavior,
  GraceTrigger,
  GracePeriodDraft,
  RateBandDraft,
  RateCapDraft,
  RateTierDraft,
  TIME_SERIES_TYPES,
  TariffDto,
  TimeSeriesType,
} from '../models/tariff.models';

interface TariffVersionDialogData {
  tariff: TariffDto;
}

@Component({
  selector: 'app-tariff-version-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatStepperModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    TranslatePipe,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './tariff-version-dialog.component.html',
  styleUrl: './tariff-version-dialog.component.scss',
})
export class TariffVersionDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly ref = inject(MatDialogRef<TariffVersionDialogComponent>);
  private readonly t = inject(TranslateService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly versionApi = inject(TariffVersionService);
  readonly data = inject<TariffVersionDialogData>(MAT_DIALOG_DATA);

  // ---- Step 1: Validity ----
  readonly validityForm = this.fb.group({
    validFrom: [new Date(), Validators.required],
    validTo: [null as Date | null],
  });

  // ---- Step 2: Pricing ----
  readonly pricingForm = this.fb.group({
    baseAmount: [0, [Validators.required, Validators.min(0)]],
    baseCurrency: [
      this.data.tariff.currency || 'EUR',
      [Validators.required, Validators.minLength(3), Validators.maxLength(3)],
    ],
    timeBasis: [TimeSeriesType.DurationBased as TimeSeriesType, Validators.required],
    interval: [BillingInterval.Minute as BillingInterval, Validators.required],
    intervalCount: [30, [Validators.required, Validators.min(1)]],
    // Entries one paid step covers: 1 = single entry (today's behaviour), 0 = unlimited, n = n entries.
    allowedEntries: [1, [Validators.required, Validators.min(0)]],
  });
  readonly timeSeriesTypes = TIME_SERIES_TYPES;
  readonly billingIntervals = BILLING_INTERVALS;

  // ---- Step 3: Grace periods (staged, submitted after version creation) ----
  readonly gracePeriodDraft = this.fb.group({
    durationMinutes: [15, [Validators.required, Validators.min(1)]],
    trigger: [GraceTrigger.EntryStart as GraceTrigger, Validators.required],
    behavior: [GraceBehavior.FreeThenFullRate as GraceBehavior, Validators.required],
  });
  readonly graceTriggers = GRACE_TRIGGERS;
  readonly graceBehaviors = GRACE_BEHAVIORS;
  readonly stagedGracePeriods = signal<GracePeriodDraft[]>([]);

  // ---- Step 4: Complex pricing — tiers and caps (staged, submitted after version creation) ----
  //
  // D-34: tiers apply marginally (steps at or after the threshold are charged the new rate; earlier
  // steps keep what they were charged) and caps bound each window's total, shortest window first.
  // Neither carries a currency here: the server derives it from the policy's base currency, and a
  // client-supplied one could only agree or be wrong.
  readonly rateTierDraft = this.fb.group({
    afterMinutes: [60, [Validators.required, Validators.min(1)]],
    newAmount: [0, [Validators.required, Validators.min(0)]],
  });
  // D-42: no anchor control — every authored cap is RollingFromEntry, and the server refuses
  // anything else. Offering a choice we cannot price correctly is worse than offering none.
  readonly rateCapDraft = this.fb.group({
    window: [CapWindow.Daily as CapWindow, Validators.required],
    maxAmount: [0, [Validators.required, Validators.min(0)]],
  });
  readonly capWindows = CAP_WINDOWS;
  readonly stagedRateTiers = signal<RateTierDraft[]>([]);
  readonly stagedRateCaps = signal<RateCapDraft[]>([]);
  readonly complexPricingError = signal<string | null>(null);

  // Time-of-day bands (Tag-/Nachttarif): each step is priced by the band its start falls in, read in
  // this time zone. The zone is sent with the version itself, because a band is meaningless without it.
  readonly timeZoneId = new FormControl(DEFAULT_TARIFF_TIME_ZONE, { nonNullable: true });
  readonly rateBandDraft = this.fb.group({
    startTime: ['06:00', Validators.required],
    endTime: ['19:00', Validators.required],
    amount: [0, [Validators.required, Validators.min(0)]],
  });
  readonly stagedRateBands = signal<RateBandDraft[]>([]);

  // ---- Submit ----
  readonly isSubmitting = signal(false);
  readonly submitError = signal<string | null>(null);

  addGracePeriod(): void {
    if (this.gracePeriodDraft.invalid) {
      this.gracePeriodDraft.markAllAsTouched();
      return;
    }
    const v = this.gracePeriodDraft.getRawValue();
    this.stagedGracePeriods.update((list) => [
      ...list,
      {
        durationMinutes: Number(v.durationMinutes ?? 15),
        trigger: v.trigger ?? GraceTrigger.EntryStart,
        behavior: v.behavior ?? GraceBehavior.FreeThenFullRate,
      },
    ]);
    this.gracePeriodDraft.reset({
      durationMinutes: 15,
      trigger: GraceTrigger.EntryStart,
      behavior: GraceBehavior.FreeThenFullRate,
    });
  }

  removeGracePeriod(index: number): void {
    this.stagedGracePeriods.update((list) => list.filter((_, i) => i !== index));
  }

  /** A multi-entry version charges one flat amount per step; tiers, caps and bands are refused for it. */
  isMultiEntry(): boolean {
    return Number(this.pricingForm.value.allowedEntries ?? 1) !== 1;
  }

  /** Same rule as the server: nothing that re-prices individual steps on a multi-entry version. */
  private refuseOnMultiEntry(): boolean {
    if (!this.isMultiEntry()) return false;
    this.complexPricingError.set(this.t.instant('tariff.version.complexPricing.multiEntry'));
    return true;
  }

  addRateBand(): void {
    if (this.refuseOnMultiEntry()) return;
    if (this.rateBandDraft.invalid) {
      this.rateBandDraft.markAllAsTouched();
      return;
    }
    const v = this.rateBandDraft.getRawValue();
    const band: RateBandDraft = {
      startTime: v.startTime ?? '',
      endTime: v.endTime ?? '',
      amount: Number(v.amount ?? 0),
    };

    // Mirrors the server rules, so the operator hears about them before the version exists rather
    // than as a partial failure after it.
    if (this.stagedRateTiers().length > 0) {
      this.complexPricingError.set(this.t.instant('tariff.version.complexPricing.bandsAndTiers'));
      return;
    }
    if (band.startTime === band.endTime) {
      this.complexPricingError.set(this.t.instant('tariff.version.complexPricing.emptyBand'));
      return;
    }
    if (this.stagedRateBands().some((b) => bandsOverlap(b, band))) {
      this.complexPricingError.set(this.t.instant('tariff.version.complexPricing.overlappingBand'));
      return;
    }

    this.complexPricingError.set(null);
    this.stagedRateBands.update((list) =>
      [...list, band].sort((a, b) => a.startTime.localeCompare(b.startTime)),
    );
    this.rateBandDraft.reset({ startTime: band.endTime, endTime: band.startTime, amount: 0 });
  }

  removeRateBand(index: number): void {
    this.stagedRateBands.update((list) => list.filter((_, i) => i !== index));
  }

  addRateTier(): void {
    if (this.refuseOnMultiEntry()) return;
    if (this.rateTierDraft.invalid) {
      this.rateTierDraft.markAllAsTouched();
      return;
    }
    const v = this.rateTierDraft.getRawValue();
    const afterMinutes = Number(v.afterMinutes ?? 0);

    if (this.stagedRateBands().length > 0) {
      this.complexPricingError.set(this.t.instant('tariff.version.complexPricing.bandsAndTiers'));
      return;
    }

    // Mirrors the server rule: two tiers at the same threshold are contradictory configuration, not
    // a stricter one. Catching it here saves the operator a 400 after the version already exists.
    if (this.stagedRateTiers().some((t) => t.afterMinutes === afterMinutes)) {
      this.complexPricingError.set(this.t.instant('tariff.version.complexPricing.duplicateTier'));
      return;
    }

    this.complexPricingError.set(null);
    this.stagedRateTiers.update((list) =>
      [...list, { afterMinutes, newAmount: Number(v.newAmount ?? 0) }].sort(
        (a, b) => a.afterMinutes - b.afterMinutes,
      ),
    );
    this.rateTierDraft.reset({ afterMinutes: 60, newAmount: 0 });
  }

  removeRateTier(index: number): void {
    this.stagedRateTiers.update((list) => list.filter((_, i) => i !== index));
  }

  addRateCap(): void {
    if (this.refuseOnMultiEntry()) return;
    if (this.rateCapDraft.invalid) {
      this.rateCapDraft.markAllAsTouched();
      return;
    }
    const v = this.rateCapDraft.getRawValue();
    const window = v.window ?? CapWindow.Daily;

    // One cap per (window, anchor) — the server's uniqueness rule. With a single authorable anchor
    // this reduces to one cap per window.
    if (this.stagedRateCaps().some((c) => c.window === window)) {
      this.complexPricingError.set(this.t.instant('tariff.version.complexPricing.duplicateCap'));
      return;
    }

    this.complexPricingError.set(null);
    this.stagedRateCaps.update((list) => [
      ...list,
      { window, maxAmount: Number(v.maxAmount ?? 0), windowAnchor: AUTHORABLE_CAP_ANCHOR },
    ]);
    this.rateCapDraft.reset({ window: CapWindow.Daily, maxAmount: 0 });
  }

  removeRateCap(index: number): void {
    this.stagedRateCaps.update((list) => list.filter((_, i) => i !== index));
  }

  async submit(): Promise<void> {
    if (this.validityForm.invalid || this.pricingForm.invalid) {
      this.validityForm.markAllAsTouched();
      this.pricingForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    this.submitError.set(null);

    const validity = this.validityForm.getRawValue();
    const pricing = this.pricingForm.getRawValue();
    const validFrom = validity.validFrom as Date;
    const validTo = validity.validTo as Date | null;

    if (validTo && validTo <= validFrom) {
      this.isSubmitting.set(false);
      this.submitError.set(this.t.instant('tariff.version.validToError'));
      return;
    }

    const request: AddTariffVersionRequest = {
      validFrom: validFrom.toISOString(),
      validTo: validTo ? validTo.toISOString() : null,
      baseAmount: Number(pricing.baseAmount ?? 0),
      baseCurrency: (pricing.baseCurrency ?? 'EUR').toUpperCase(),
      timeBasis: pricing.timeBasis ?? TimeSeriesType.DurationBased,
      interval: pricing.interval ?? BillingInterval.Minute,
      intervalCount: Number(pricing.intervalCount ?? 30),
      timeZoneId: this.timeZoneId.value.trim() || null,
      allowedEntries: Number(pricing.allowedEntries ?? 1),
    };

    // The entries setting can be changed after tiers/caps/bands were staged; the server would refuse
    // every one of them after the version already exists, so stop here instead.
    if (
      request.allowedEntries !== 1 &&
      (this.stagedRateTiers().length > 0 || this.stagedRateCaps().length > 0 || this.stagedRateBands().length > 0)
    ) {
      this.isSubmitting.set(false);
      this.submitError.set(this.t.instant('tariff.version.complexPricing.multiEntry'));
      return;
    }

    if (this.stagedRateBands().length > 0 && !request.timeZoneId) {
      this.isSubmitting.set(false);
      this.submitError.set(this.t.instant('tariff.version.complexPricing.timeZoneRequired'));
      return;
    }

    try {
      const version = await firstValueFrom(this.versionApi.tariffsTariffIdVersionsPost(this.data.tariff.id!, request));
      const versionId = version?.id;
      const graces = this.stagedGracePeriods();

      if (versionId && graces.length > 0) {
        let failures = 0;
        for (const g of graces) {
          const graceRequest: AddGracePeriodRequest = {
            durationMinutes: g.durationMinutes,
            trigger: g.trigger,
            behavior: g.behavior,
          };
          try {
            await firstValueFrom(
              this.versionApi.tariffsTariffIdVersionsIdGracePeriodsPost(this.data.tariff.id!, versionId, graceRequest),
            );
          } catch {
            failures++;
          }
        }
        if (failures > 0) {
          this.snackBar.open(
            this.t.instant('tariff.version.msg.partialGrace', { failed: failures, total: graces.length }),
            'Close',
            { duration: 6000 },
          );
        }
      }

      // D-34 step 4: tiers and caps are children of the pricing policy, so they can only be attached
      // once the version exists. The version itself is already created at this point — a failure here
      // leaves a valid version with incomplete complex pricing, which the operator can finish from
      // the version list rather than by re-creating the version.
      const tiers = this.stagedRateTiers();
      const caps = this.stagedRateCaps();
      const bands = this.stagedRateBands();

      if (versionId && (tiers.length > 0 || caps.length > 0 || bands.length > 0)) {
        let failures = 0;

        for (const band of bands) {
          const bandRequest: AddRateBandRequest = {
            startTime: toApiTime(band.startTime),
            endTime: toApiTime(band.endTime),
            amount: band.amount,
          };
          try {
            await firstValueFrom(
              this.versionApi.tariffsTariffIdVersionsIdRateBandsPost(this.data.tariff.id!, versionId, bandRequest),
            );
          } catch {
            failures++;
          }
        }

        for (const tier of tiers) {
          const tierRequest: AddRateTierRequest = {
            afterMinutes: tier.afterMinutes,
            newAmount: tier.newAmount,
          };
          try {
            await firstValueFrom(
              this.versionApi.tariffsTariffIdVersionsIdRateTiersPost(this.data.tariff.id!, versionId, tierRequest),
            );
          } catch {
            failures++;
          }
        }

        for (const cap of caps) {
          const capRequest: AddRateCapRequest = {
            window: cap.window,
            maxAmount: cap.maxAmount,
            windowAnchor: cap.windowAnchor,
          };
          try {
            await firstValueFrom(
              this.versionApi.tariffsTariffIdVersionsIdRateCapsPost(this.data.tariff.id!, versionId, capRequest),
            );
          } catch {
            failures++;
          }
        }

        if (failures > 0) {
          this.snackBar.open(
            this.t.instant('tariff.version.msg.partialComplexPricing', {
              failed: failures,
              total: tiers.length + caps.length + bands.length,
            }),
            'Close',
            { duration: 6000 },
          );
        }
      }

      this.isSubmitting.set(false);
      this.ref.close(true);
    } catch (err) {
      this.isSubmitting.set(false);
      const detail = (err as { error?: { detail?: string; title?: string } })?.error?.detail
        ?? (err as { error?: { detail?: string; title?: string } })?.error?.title
        ?? this.t.instant('tariff.version.msg.createFailed');
      this.submitError.set(detail);
    }
  }

  entriesLabel(entries: number | null | undefined): string {
    const n = Number(entries ?? 1);
    if (n === 0) return this.t.instant('tariff.version.entries.unlimited');
    if (n === 1) return this.t.instant('tariff.version.entries.single');
    return this.t.instant('tariff.version.entries.count', { count: n });
  }

  cancel(): void {
    this.ref.close();
  }
}

/** "HH:mm" from the time input → the "HH:mm:ss" the API's TimeOnly binds. */
function toApiTime(time: string): string {
  return time.length === 5 ? `${time}:00` : time;
}

/** Minute-of-day ranges of a band, split in two when it wraps midnight (19:00 → 06:00). */
function bandRanges(band: RateBandDraft): Array<[number, number]> {
  const toMinute = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const from = toMinute(band.startTime);
  const to = band.endTime === '00:00' ? 1440 : toMinute(band.endTime);
  return from < to ? [[from, to]] : [[from, 1440], ...(to > 0 ? [[0, to] as [number, number]] : [])];
}

/** Same rule as RateBand.Overlaps on the server: any shared minute of the day. */
function bandsOverlap(a: RateBandDraft, b: RateBandDraft): boolean {
  return bandRanges(a).some(([af, at]) => bandRanges(b).some(([bf, bt]) => af < bt && bf < at));
}
